import React, { useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { type AttributeValue } from "@/schemas/attribute/AttributeSet";
import { EventAttributeType } from "@/schemas/events/EventAttributeSet";
import { type EventFilterOption } from "@/schemas/events/filter/EventFilterOption";
import { type FilterBySelection } from "@/schemas/screen/filters/FilterBySelection";

interface FilterCardProps<T> {
  selection: FilterBySelection<T> | null;
  onSetFilters: (
    newSelection: FilterBySelection<T>,
    attribute: EventAttributeType,
  ) => void;
  option: EventFilterOption;
}

export function FilterCard<T>({
  selection,
  onSetFilters,
  option,
}: FilterCardProps<T>): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const selectedValues = selection?.values ?? [];

  const handleSelect = (item: AttributeValue): void => {
    const newSelection = {
      values: selectedValues.includes(item.id as T)
        ? selectedValues.filter((id) => id !== item.id)
        : [...selectedValues, item.id],
    } as FilterBySelection<T>;
    onSetFilters(newSelection, option.attribute.id);
  };

  const getSelectedCategoriesLabel = (): string => {
    if (selectedValues.length === 0) return "";
    const selectedLabels = selectedValues.map(
      (id) =>
        option.attribute.map[id as keyof typeof option.attribute.map]?.label ??
        "",
    );
    if (selectedLabels.length === 1) return selectedLabels[0] ?? "";
    return `${selectedLabels.length} selected`;
  };

  return (
    <View style={styles.filterSection}>
      <TouchableOpacity
        style={[styles.filterHeader]}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <View style={styles.filterIconContainer}>
          <Text style={styles.icon}>{option.icon}</Text>
        </View>
        <View style={styles.filterLabelContainer}>
          <Text style={styles.filterLabel}>{option.label}</Text>
          {selectedValues.length > 0 && (
            <Text style={styles.filterValue}>
              {getSelectedCategoriesLabel()}
            </Text>
          )}
        </View>
        <Ionicons
          name={isExpanded ? "chevron-up" : "chevron-down"}
          size={24}
          color="#6B7280"
        />
      </TouchableOpacity>
      {isExpanded && (
        <View style={styles.filterContent}>
          <View style={styles.selectionGrid}>
            {Object.values(option.attribute.map).map((option) => (
              <TouchableOpacity
                key={option.id}
                style={[
                  styles.optionCard,
                  selectedValues.includes(option.id as T) &&
                    styles.selectedCard,
                ]}
                onPress={() => handleSelect(option)}
              >
                <Text style={styles.optionIcon}>{option.iconLabel}</Text>
                <Text style={[styles.optionLabel]}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  filterSection: {
    marginBottom: 15,
  },
  filterHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    padding: 15,
  },
  filterContent: {
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    padding: 12,
    marginTop: 10,
  },
  filterIconContainer: {
    backgroundColor: "#E5E7EB",
    borderRadius: 20,
    padding: 8,
  },
  filterLabelContainer: {
    flex: 1,
    marginLeft: 15,
  },
  filterLabel: {
    fontSize: 16,
    fontWeight: "bold",
  },
  filterValue: {
    fontSize: 14,
    color: "#1F2937",
    marginTop: 2,
  },
  selectionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 15,
  },
  optionCard: {
    width: "31%",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    marginBottom: 8,
  },
  selectedCard: {
    backgroundColor: "#EBF5FF",
    borderColor: "#3B82F6",
  },
  optionIcon: {
    fontSize: 24,
  },
  optionLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: "#1F2937",
    marginTop: 8,
    textAlign: "center",
  },
  icon: {
    fontSize: 24,
    fontWeight: "bold",
  },
});

export default FilterCard;
